import { create } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  CheckReleaseDeprecateBlockersRequestSchema,
  DeprecateReleaseRequestSchema,
  GetReleaseByIDRequestSchema,
  ListReleasesRequestSchema,
  ReleaseSyncStatus,
  ReleasesService,
  StreamReleaseEventsRequestSchema,
  StreamWorkflowRunEventsRequestSchema,
  SyncReleaseRequestSchema,
  UpdateReleaseStateRequestSchema,
} from '../gen/releases/v1/releases_pb';
import { createApiTransport, createAuthApiTransport } from '../transport';
import { isNonRetriable, streamSleep } from './streamingUtils';

export { ReleaseSyncStatus } from '../gen/releases/v1/releases_pb';

/** Terminal outcomes of a release sync/promote/plan run. Narrowly scoped to
 * SUCCEEDED/FAILED (excludes IN_PROGRESS) - callers needing to also treat
 * IN_PROGRESS as "settled" (e.g. clearing an optimistic pending flag) should
 * check that separately, since folding it in here would change this set's
 * meaning for the terminal-only call site. Mirrors the
 * IN_FLIGHT_STATES/ACTION_BLOCKING_STATES idiom in
 * features/workspaces/WorkspaceVersionStatus.tsx. */
export const RELEASE_SYNC_TERMINAL_STATES = new Set<ReleaseSyncStatus | undefined>([
  ReleaseSyncStatus.SUCCEEDED,
  ReleaseSyncStatus.FAILED,
]);

function transport(token?: string | null) {
  return token ? createAuthApiTransport(token) : createApiTransport();
}

// How long useStreamWorkflowRunEvents waits for a "completed" event before
// giving up - generous (matches the order of magnitude of a real Terraform
// apply/plan run), but bounded so a dropped connection the server-side
// keepalive doesn't catch (e.g. an intermediate proxy killing the stream
// outright) doesn't leave the UI spinning forever.
const STREAM_COMPLETION_TIMEOUT_MS = 20 * 60 * 1000;

export interface ListReleasesOptions {
  deployableOnly?: boolean;
  includeDeprecated?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
}

export function useListReleases(
  token?: string | null,
  releaseGroup?: string,
  options?: ListReleasesOptions,
) {
  const deployableOnly = options?.deployableOnly ?? false;
  const includeDeprecated = options?.includeDeprecated ?? false;
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? 0;
  const search = options?.search ?? '';

  return useQuery({
    queryKey: [
      'releases',
      'list',
      !!token,
      releaseGroup ?? '',
      deployableOnly,
      includeDeprecated,
      page,
      pageSize,
      search,
    ],
    queryFn: async () => {
      const client = createClient(ReleasesService, transport(token));
      const request = create(ListReleasesRequestSchema, {
        releaseGroup: releaseGroup ?? '',
        deployableOnly,
        includeDeprecated,
        page,
        pageSize,
        search,
      });
      return await client.listReleases(request);
    },
    enabled: !!token,
  });
}

/**
 * Fetches a single release by ID. Live updates come from
 * useStreamReleaseEvents' cache invalidation (see below), not polling - the
 * consuming page must call that hook for this query to refresh while a sync
 * is in flight. Pass `releaseId: undefined` to leave the query disabled.
 */
export function useGetReleaseById(token?: string | null, releaseId?: string | null) {
  return useQuery({
    queryKey: ['releases', 'byId', !!token, releaseId ?? ''],
    queryFn: async () => {
      const client = createClient(ReleasesService, transport(token));
      const request = create(GetReleaseByIDRequestSchema, {
        releaseId: releaseId ?? '',
      });
      return await client.getReleaseByID(request);
    },
    enabled: !!token && !!releaseId,
  });
}

/**
 * Toggles a release's enabled/disabled state. Platform-admin-only on the
 * server — the UI must gate visibility of any button calling this the same
 * way (useAuthorization().isPlatformAdmin).
 */
export function useUpdateReleaseState(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { releaseId: string; isEnabled: boolean }) => {
      const client = createClient(ReleasesService, transport(token));
      const request = create(UpdateReleaseStateRequestSchema, {
        releaseId: input.releaseId,
        isEnabled: input.isEnabled,
      });
      return await client.updateReleaseState(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      // useListAvailableReleases lives under the 'workspaces' key root, not
      // 'releases' — without this, an open upgrade tab wouldn't see an
      // enable/disable toggle until staleTime lapses or it remounts.
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
}

/**
 * Marks a release deprecated so it can no longer be selected for a new
 * install or upgrade in any environment. Operator-admin-only on the server,
 * same gating requirement as useUpdateReleaseState. Irreversible via the API
 * (no "un-deprecate" RPC exists). Rejects (throws) if one or more workspaces
 * still depend on this release (current, or an in-flight promote target) -
 * pair this with useCheckReleaseDeprecateBlockers to pre-populate a
 * confirmation dialog with the blocking tenants, and handle a thrown error
 * the same way as any other mutation (see WorkspacePage.tsx's onError
 * pattern) for the rare case a race slips past that pre-check.
 */
export function useDeprecateRelease(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { releaseId: string }) => {
      const client = createClient(ReleasesService, transport(token));
      const request = create(DeprecateReleaseRequestSchema, {
        releaseId: input.releaseId,
      });
      return await client.deprecateRelease(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      // useListAvailableReleases lives under the 'workspaces' key root, not
      // 'releases' — without this, an open upgrade tab wouldn't see a
      // deprecation until staleTime lapses or it remounts.
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
}

/**
 * Reports how many workspaces currently block deprecating this release - a
 * workspace still has it as current_release_id, or an in-flight
 * promote/upgrade targets it. A count of 0 means deprecation is allowed.
 * Intended to be polled while a deprecate confirmation dialog is open (pass
 * `enabled` tied to the dialog's open state), mirroring
 * useCheckDeploymentDeleteBlockers.
 */
export function useCheckReleaseDeprecateBlockers(
  releaseId?: string,
  token?: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['releases', releaseId ?? '', 'deprecate-blockers', !!token],
    queryFn: async () => {
      const client = createClient(ReleasesService, transport(token));
      const request = create(CheckReleaseDeprecateBlockersRequestSchema, {
        releaseId: releaseId ?? '',
      });
      return await client.checkReleaseDeprecateBlockers(request);
    },
    enabled: !!releaseId && !!token && enabled,
  });
}

/**
 * Dispatches a release-level manifest sync (weekly_release_sync.yml against
 * the release's own branch) and returns immediately with sync_status
 * IN_PROGRESS. Admin-only on the server, same gating requirement as
 * useUpdateReleaseState. Callers should watch useGetReleaseById/useListReleases'
 * live updates for completion rather than expecting this mutation to resolve
 * once the sync is actually done.
 *
 * Deliberately has no onSuccess cache invalidation: the sync-started audit
 * row this dispatches through startReleaseSync already fires the
 * release_audit_notify DB trigger, which useStreamReleaseEvents picks up and
 * invalidates ['releases'] from - live, the moment the DB transaction
 * commits. A second invalidate here raced that SSE-driven one and caused
 * useGetReleaseById to refetch twice per sync.
 */
export function useSyncRelease(token?: string | null) {
  return useMutation({
    mutationFn: async (input: { releaseId: string }) => {
      const client = createClient(ReleasesService, transport(token));
      const request = create(SyncReleaseRequestSchema, {
        releaseId: input.releaseId,
      });
      return await client.syncRelease(request);
    },
  });
}

/**
 * Subscribes to the release-announcement SSE stream and invalidates React Query
 * caches when an event arrives. Workspace lifecycle flags (`is_deprecated`,
 * `is_latest`) are computed server-side, so the stream's payload is just a
 * "something changed" signal — refetch and let the server re-derive state.
 */
export function useStreamReleaseEvents(accessToken: string | null) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!accessToken) return;

    let cancelled = false;
    const abortController = new AbortController();

    async function listen() {
      while (!cancelled) {
        try {
          const t = createAuthApiTransport(accessToken!);
          const client = createClient(ReleasesService, t);
          const request = create(StreamReleaseEventsRequestSchema, {});

          for await (const event of client.streamReleaseEvents(request, {
            signal: abortController.signal,
          })) {
            if (cancelled) return;
            try {
              const p = event.payload ? (JSON.parse(event.payload) as { event_type?: string }) : {};
              if (p.event_type === 'keepalive') continue;
            } catch {
              // malformed payload — treat as a change signal
            }
            queryClient.invalidateQueries({ queryKey: ['workspaces'] });
            queryClient.invalidateQueries({ queryKey: ['releases'] });
          }
        } catch (err) {
          if (cancelled || isNonRetriable(err)) return;
        }
        await streamSleep(5000, abortController.signal).catch(() => {});
      }
    }

    listen();
    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [accessToken, queryClient]);
}

/** One event delivered over StreamWorkflowRunEvents - either a log line
 * ("log") or the terminal outcome ("completed", conclusion carried in `message`). */
export interface WorkflowRunLogEvent {
  eventType: string;
  severity: string;
  message: string;
  timestampMs: number;
}

/**
 * Subscribes to live log/status events for a single workflow run (e.g. a
 * release plan dispatch) and accumulates them into local state. Unlike
 * useStreamReleaseEvents/useStreamWorkspaceEvents - which loop forever and
 * only use receipt of an event as a cache-invalidation signal - this hook
 * renders the actual payload and stops reconnecting once a "completed" event
 * arrives, since the run is then over and nothing further will be published
 * to its channel.
 *
 * Keyed by temporal_workflow_id (the deterministic Temporal workflow ID,
 * e.g. release-plan-<workspace_id>) rather than a GitHub-assigned wrun_id, so
 * the caller can subscribe immediately from a dispatch RPC's response,
 * before GitHub Actions has registered a run. Since that ID is reused across
 * every dispatch for the same workspace, sinceMs (the dispatch's own
 * dispatched_at_ms) must be passed through so a stale, already-completed
 * prior run's row isn't mistaken for the current one.
 *
 * Resets accumulated events whenever temporalWorkflowId changes (including
 * to/from undefined), so switching to a new run's stream doesn't show stale
 * output from the previous one.
 *
 * Gives up after STREAM_COMPLETION_TIMEOUT_MS without a "completed" event -
 * `isTimedOut` on the return value tells the caller the run may have
 * finished on the backend without the frontend ever finding out (e.g. an
 * intermediate proxy dropped the connection despite the server's keepalive),
 * rather than leaving a spinner running forever.
 *
 * @param temporalWorkflowId - deterministic Temporal workflow ID to subscribe to; pass undefined/empty to stay idle
 * @param sinceMs - dispatched_at_ms from the dispatch RPC's response; 0/undefined means no staleness cutoff
 * @param accessToken - Auth0 access token
 */
export function useStreamWorkflowRunEvents(
  temporalWorkflowId: string | undefined,
  sinceMs: number | undefined,
  accessToken: string | null | undefined,
) {
  const [events, setEvents] = useState<WorkflowRunLogEvent[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);

  useEffect(() => {
    setEvents([]);
    setIsComplete(false);
    setIsTimedOut(false);

    if (!accessToken || !temporalWorkflowId) return;

    let cancelled = false;
    const abortController = new AbortController();

    const timeoutTimer = setTimeout(() => {
      if (cancelled) return;
      cancelled = true;
      setIsTimedOut(true);
      abortController.abort();
    }, STREAM_COMPLETION_TIMEOUT_MS);

    async function listen() {
      while (!cancelled) {
        try {
          const t = createAuthApiTransport(accessToken!);
          const client = createClient(ReleasesService, t);
          const request = create(StreamWorkflowRunEventsRequestSchema, {
            temporalWorkflowId,
            sinceMs: BigInt(sinceMs ?? 0),
          });

          for await (const event of client.streamWorkflowRunEvents(request, {
            signal: abortController.signal,
          })) {
            if (cancelled) return;
            if (event.eventType === 'keepalive') continue;
            setEvents((prev) => [
              ...prev,
              {
                eventType: event.eventType,
                severity: event.severity,
                message: event.message,
                timestampMs: Number(event.timestampMs),
              },
            ]);
            if (event.eventType === 'completed') {
              setIsComplete(true);
              cancelled = true;
              clearTimeout(timeoutTimer);
              abortController.abort();
              return;
            }
          }
        } catch (err) {
          if (cancelled || isNonRetriable(err)) return;
        }
        await streamSleep(5000, abortController.signal).catch(() => {});
      }
    }

    listen();
    return () => {
      cancelled = true;
      abortController.abort();
      clearTimeout(timeoutTimer);
    };
  }, [temporalWorkflowId, sinceMs, accessToken]);

  return { events, isComplete, isTimedOut };
}
